import { httpRequest } from "@services/http"

/**
 * Moving a campaign's dates, and everything that moves with them (MPN-2040).
 *
 * The preview and the save run the SAME calculation on the server, so the confirmation
 * cannot describe something other than what Save does — which is the only reason showing it
 * is worth anything.
 */
export type CampaignDateAction = "follow" | "trim" | "unschedule"

export type DateEntryEffect = {
  entry_id: number
  name: string
  /** "ad group" or "ad". */
  kind: string
  start: string
  end: string
  action: CampaignDateAction
  new_start?: string | null
  new_end?: string | null
  ad_group_id?: number | null
}

export type DateWindowEffect = {
  segment_id: number
  name: string
  start: string
  end: string
  action: CampaignDateAction
  amount: number
  new_start?: string | null
  new_end?: string | null
  /** What the window keeps. The difference returns to Campaign unallocated. */
  new_amount: number
}

export type DateKeyDateFlag = { key_date_id: number; label: string; date: string }

export type CampaignDatesPreview = {
  old_start?: string | null
  old_end?: string | null
  start: string
  end: string
  ad_groups: DateEntryEffect[]
  ad_flights: DateEntryEffect[]
  windows: DateWindowEffect[]
  key_dates: DateKeyDateFlag[]
  /** Set when the new end outlasts the contract. A warning, never a block. */
  contract_ends?: string | null
}

const base = (launchId: number) =>
  `${import.meta.env.VITE_API_PLATFORM_SERVICE_URL}api/v1/engagements/launches/${launchId}/dates/`

export class CommandPreviewCampaignDates {
  async run(launchId: number, start: string, end: string): Promise<CampaignDatesPreview> {
    return httpRequest<CampaignDatesPreview>(`${base(launchId)}preview/`, {
      method: "POST",
      body: { start, end },
    })
  }
}

export class CommandSetCampaignDates {
  /**
   * `followAdGroupIds` is the operator's answer to the preview's checkboxes. Undefined
   * means all of them, which is what the preview pre-checks; an empty array means none.
   */
  async run(
    launchId: number,
    start: string,
    end: string,
    followAdGroupIds?: number[],
    note?: string,
  ): Promise<CampaignDatesPreview> {
    return httpRequest<CampaignDatesPreview>(base(launchId), {
      method: "PUT",
      body: {
        start,
        end,
        follow_ad_group_ids: followAdGroupIds ?? null,
        note: note ?? "",
      },
    })
  }
}
